import React from 'react';
import { motion } from 'framer-motion';
import { Webhook, BrainCircuit, Send, CheckCircle2, Database } from 'lucide-react';

const nodes = [
    { icon: Webhook, label: 'New lead', sub: 'Webhook trigger', tint: 'text-emerald-400', ring: 'ring-emerald-400/30 bg-emerald-400/10' },
    { icon: BrainCircuit, label: 'AI qualify & enrich', sub: 'GPT-4 agent', tint: 'text-brand-cyan', ring: 'ring-brand-cyan/30 bg-brand-cyan/10' },
    { icon: Database, label: 'Sync to CRM', sub: 'HubSpot', tint: 'text-brand-blue', ring: 'ring-brand-blue/30 bg-brand-blue/10' },
    { icon: Send, label: 'Notify sales team', sub: 'WhatsApp + Slack', tint: 'text-brand-orange', ring: 'ring-brand-orange/30 bg-brand-orange/10' },
];

/** A stylised, always-running automation pipeline used as the hero centrepiece. */
const WorkflowVisual: React.FC = () => {
    return (
        <div className="relative w-full max-w-md mx-auto">
            {/* glow */}
            <div className="absolute -inset-6 bg-gradient-to-br from-brand-cyan/20 via-brand-blue/10 to-transparent rounded-[2rem] blur-3xl opacity-70 pointer-events-none" />

            <div className="relative rounded-2xl glass shadow-lift overflow-hidden">
                {/* window chrome */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06] bg-white/[0.02]">
                    <div className="flex gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-red-500/70" />
                        <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
                        <span className="w-3 h-3 rounded-full bg-green-500/70" />
                    </div>
                    <div className="flex items-center gap-2 text-xs font-mono text-text-muted">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        workflow · live
                    </div>
                </div>

                {/* pipeline */}
                <div className="p-5 space-y-0">
                    {nodes.map((node, i) => (
                        <div key={node.label}>
                            <motion.div
                                initial={{ opacity: 0, x: -12 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.15 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                className="flex items-center gap-4 rounded-xl bg-white/[0.03] border border-white/[0.05] p-3"
                            >
                                <div className={`w-11 h-11 rounded-xl grid place-items-center ring-1 ${node.ring} shrink-0`}>
                                    <node.icon className={`w-5 h-5 ${node.tint}`} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-white truncate">{node.label}</div>
                                    <div className="text-xs text-text-faint font-mono truncate">{node.sub}</div>
                                </div>
                                <CheckCircle2 className="w-4 h-4 text-emerald-400/80 shrink-0" />
                            </motion.div>

                            {i < nodes.length - 1 && (
                                <div className="relative h-6 ml-[1.55rem] w-px bg-gradient-to-b from-white/20 to-white/5 overflow-visible">
                                    {/* travelling data packet */}
                                    <motion.span
                                        className="absolute -left-[3px] w-[7px] h-[7px] rounded-full bg-brand-cyan shadow-glow-cyan"
                                        initial={{ top: 0, opacity: 0 }}
                                        animate={{ top: ['0%', '100%'], opacity: [0, 1, 0] }}
                                        transition={{
                                            duration: 1.4,
                                            repeat: Infinity,
                                            delay: i * 0.5,
                                            ease: 'easeInOut',
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* footer stat */}
                <div className="flex items-center justify-between px-5 py-3.5 border-t border-white/[0.06] bg-white/[0.02] text-xs">
                    <span className="text-text-muted">Avg. run time</span>
                    <span className="font-mono text-emerald-400">1.2s · 100% success</span>
                </div>
            </div>

            {/* floating badge */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="absolute -right-4 -bottom-4 sm:-right-8 rounded-2xl glass px-4 py-3 shadow-card animate-float"
            >
                <div className="text-xs text-text-muted">Manual hours saved / wk</div>
                <div className="text-2xl font-display font-bold text-white">
                    +38<span className="text-brand-cyan">h</span>
                </div>
            </motion.div>
        </div>
    );
};

export default WorkflowVisual;
